import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../contexts/CartContext';
import BackHeader from '../components/BackHeader';
import RestaurantDetailCard from '../components/RestaurantDetailCard';
import NavigationBar from '../components/NavigationBar';
import ProductSection from '../components/ProductSection';
import CartBottomSheet from '../components/CartBottomSheet';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';
import SectionNavigationBar from '../components/SectionNavigationBar';
import { getStatusMessages } from '../config/constants';
import useTranslation from '../hooks/useTranslation';
import { getCurrencyFromLanguage } from '../utils/currencyUtils';
import { getCurrentLanguage } from '../config/languageConfig';

const GiftShopDetail: React.FC = () => {
  // 기프트샵은 항상 'gift-shop-1' ID를 사용
  const restaurantId = 'gift-shop-1';
  const navigate = useNavigate();
  const { setRestaurantMinOrderAmount } = useCart();
  const { t } = useTranslation();
  
  // 스크롤 관련 상태
  const [activeSectionId, setActiveSectionId] = useState<string>('');
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const sectionNavRef = useRef<HTMLDivElement>(null);
  const isUserScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // 현재 언어에 맞는 통화 설정
  const currentLang = getCurrentLanguage();
  const selectedCurrency = getCurrencyFromLanguage(currentLang);
  
  // 다국어 상태 메시지 가져오기
  const STATUS_MESSAGES = getStatusMessages();
  
  // 기프트샵 정보 및 상품 가져오기
  const { productSections, giftShopInfo, loading, error, refetch } = useProducts(restaurantId);
  
  // 최소 주문 금액 - 현재 언어에 맞는 통화의 최소 주문 금액 사용
  const minOrderAmount = useMemo(() => {
    if (!giftShopInfo) return null;
    
    // 현재 언어에 맞는 통화 결정
    const currency = getCurrencyFromLanguage(currentLang);
    
    // 통화에 맞는 필드 이름 구성 (예: minOrderAmountKrw, minOrderAmountUsd 등)
    const currencyCode = currency.charAt(0).toUpperCase() + currency.slice(1).toLowerCase();
    const minOrderAmountField = `minOrderAmount${currencyCode}`;
    
    // 언어에 맞는 최소 주문금액이 있으면 사용, 없으면 기본 최소 주문금액 사용
    return giftShopInfo[minOrderAmountField as keyof typeof giftShopInfo] as number || giftShopInfo.minOrderAmount;
  }, [giftShopInfo, currentLang]);
  
  // CartContext에 최소 주문 금액 저장
  useEffect(() => {
    if (minOrderAmount !== null && giftShopInfo) {
      // 서버 응답에서 각 통화별 최소 주문 금액 가져오기
      const minOrderAmounts = {
        krw: giftShopInfo.minOrderAmountKrw || giftShopInfo.minOrderAmount || 0,
        usd: giftShopInfo.minOrderAmountUsd || giftShopInfo.minOrderAmount || 0,
        jpy: giftShopInfo.minOrderAmountJpy || giftShopInfo.minOrderAmount || 0,
        cny: giftShopInfo.minOrderAmountCny || giftShopInfo.minOrderAmount || 0,
        twd: giftShopInfo.minOrderAmountTwd || giftShopInfo.minOrderAmount || 0,
      };
      
      setRestaurantMinOrderAmount(minOrderAmounts);
    }
  }, [minOrderAmount, giftShopInfo, setRestaurantMinOrderAmount]);
  
  // 현재 언어에 맞는 기프트샵 이름 가져오기
  const displayName = useMemo(() => {
    if (loading || !giftShopInfo) return '[Gift Shop]';
    
    // 현재 언어에 맞는 이름 확인
    switch(currentLang) {
      case 'ko':
        if (giftShopInfo.nameKo) return giftShopInfo.nameKo;
        break;
      case 'ja':
        if (giftShopInfo.nameJa) return giftShopInfo.nameJa;
        break;
      case 'zh-CN':
        if (giftShopInfo.nameZhCn) return giftShopInfo.nameZhCn;
        break;
      case 'zh-TW':
        if (giftShopInfo.nameZhTw) return giftShopInfo.nameZhTw;
        break;
      // 'en'은 기본 fallback이므로 아래에서 처리
    }
    
    // 현재 언어가 영어가 아니고, 영어 이름을 사용할 경우 영어 표시 추가
    if (giftShopInfo.nameEn) {
      if (currentLang !== 'en') {
        return `${giftShopInfo.nameEn}`;
      }
      return giftShopInfo.nameEn;
    }
    
    // 영문도 없으면 기본 이름 사용
    return giftShopInfo.name;
  }, [giftShopInfo, loading, currentLang]);
  
  // 장바구니 페이지로 이동
  const handleCheckout = () => {
    navigate('/cart', { replace: false });
  };
  
  // 기프트샵 다국어 이름 정보
  const shopMultiNames = useMemo(() => {
    if (!giftShopInfo) return undefined;
    
    return {
      name: giftShopInfo.name,
      nameEn: giftShopInfo.nameEn,
      nameKo: giftShopInfo.nameKo,
      nameJa: giftShopInfo.nameJa,
      nameZhCn: giftShopInfo.nameZhCn,
      nameZhTw: giftShopInfo.nameZhTw
    };
  }, [giftShopInfo]);
  
  // GiftShop 정보를 Restaurant 형태로 변환 (RestaurantDetailCard에서 사용하기 위해)
  const restaurantForCard = useMemo(() => {
    if (!giftShopInfo) return null;
    
    // 타입 호환성 문제로 인해 타입 단언 사용
    return {
      ...giftShopInfo,
      // RestaurantDetailCard에서 필요한 최소 필드들 추가
      restaurantId: 0,
      phone: '',
      serviceCenterNumber: '',
      introductionTitle: '',
      introductionTitleEn: null,
      introductionTitleKo: null,
      introductionTitleJa: null,
      introductionTitleZhCn: null,
      introductionTitleZhTw: null,
      reviewAvg: 0,
      reviewCount: 0
    } as any; // 타입 단언으로 호환성 문제 해결
  }, [giftShopInfo]);
  
  // 헤더와 네비게이션 바의 실제 높이를 동적으로 계산
  const getHeaderOffset = useCallback(() => {
    const backHeader = document.querySelector('header');
    const sectionNav = sectionNavRef.current;
    
    let offset = 0;
    if (backHeader) {
      offset += backHeader.getBoundingClientRect().height;
    }
    if (sectionNav) {
      offset += sectionNav.getBoundingClientRect().height;
    }
    
    return offset + 8; // 약간의 여백 추가
  }, []);

  // 섹션 클릭 시 해당 섹션으로 스크롤
  const handleSectionClick = useCallback((sectionId: string) => {
    const sectionElement = sectionRefs.current[sectionId];
    if (sectionElement) {
      // 사용자가 직접 클릭했음을 표시
      isUserScrollingRef.current = true;
      setActiveSectionId(sectionId);
      
      const headerOffset = getHeaderOffset();
      const elementPosition = sectionElement.offsetTop;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: Math.max(0, offsetPosition),
        behavior: 'smooth'
      });
      
      // 스크롤 완료 후 자동 감지 재활성화 (약 1초 후)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 1000);
    }
  }, [getHeaderOffset]);

  // 스크롤 감지하여 현재 보이는 섹션 업데이트
  useEffect(() => {
    const handleScroll = () => {
      // 사용자가 직접 클릭한 경우 자동 감지 비활성화
      if (isUserScrollingRef.current || productSections.length === 0) return;

      const headerOffset = getHeaderOffset();
      const scrollPosition = window.scrollY + headerOffset;
      let currentSection = productSections[0]?.id || '';

      // 각 섹션의 위치를 확인하여 현재 보이는 섹션 찾기
      for (let i = productSections.length - 1; i >= 0; i--) {
        const section = productSections[i];
        const element = sectionRefs.current[section.id];
        if (element && element.offsetTop <= scrollPosition) {
          currentSection = section.id;
          break;
        }
      }

      setActiveSectionId(currentSection);
    };

    // 스크롤 이벤트에 쓰로틀링 적용
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    // 초기 섹션 설정 (약간의 지연 후)
    const timer = setTimeout(() => {
      if (productSections.length > 0 && !isUserScrollingRef.current) {
        setActiveSectionId(productSections[0].id);
      }
    }, 100);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      clearTimeout(timer);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [productSections, getHeaderOffset]);

  // 섹션 ref 설정 함수
  const setSectionRef = useCallback((sectionId: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[sectionId] = el;
  }, []);
  
  // 로딩 중 표시
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={displayName} />
        <div className="p-4">
          <LoadingState 
            message={STATUS_MESSAGES.loading.restaurants} 
            id={`gift-shop-detail-${restaurantId}`}
          />
        </div>
        <NavigationBar />
      </div>
    );
  }
  
  // 에러 표시
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.title')} />
        <div className="p-4">
          <ErrorState message={error} onRetry={() => window.location.reload()} />
        </div>
        <NavigationBar />
      </div>
    );
  }
  
  // 기프트샵 정보가 없을 때
  if (!giftShopInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.notFound')} />
        <div className="p-4">
          <EmptyState message={t('restaurant.notFound')} />
        </div>
        <NavigationBar />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* GNB */}
      <BackHeader title={displayName} />
      
      <main ref={mainContainerRef} className="pb-24">
        {/* 기프트샵 정보 카드 */}
        <div className="p-4 mb-4">
          <RestaurantDetailCard 
            restaurant={restaurantForCard} 
            currency={selectedCurrency}
            isFromCategory={false}
          />
        </div>
        
        {/* 섹션 네비게이션 바 */}
        {productSections.length > 0 && (
          <SectionNavigationBar
            ref={sectionNavRef}
            sections={productSections}
            onSectionClick={handleSectionClick}
            activeSectionId={activeSectionId}
          />
        )}
        
        {/* 상품 섹션 */}
        <div className="p-4">
          {productSections.length === 0 ? (
            <EmptyState 
              message={STATUS_MESSAGES.empty.menus} 
              subtitle={t('restaurant.noMenuItems')}
              actionText={t('action.refresh')}
              onAction={refetch}
              hideIcon={true}
            />
          ) : (
            <div className="space-y-2">
              {productSections.map(section => (
                <div
                  key={section.id}
                  ref={setSectionRef(section.id)}
                  className="scroll-mt-24"
                >
                  <ProductSection
                    section={section}
                    shopId={restaurantId}
                    shopName={displayName}
                    shopMultiNames={shopMultiNames}
                    currency={selectedCurrency}
                  />
                </div>
              ))}
              {/* 바텀 시트와의 간격을 확보하기 위한 여백 */}
              <div className="h-80"></div>
            </div>
          )}
        </div>
      </main>
      
      {/* 장바구니 바텀시트 */}
      <CartBottomSheet 
        minOrderAmount={minOrderAmount}
        onCheckout={handleCheckout}
        restaurantId={restaurantId}
        currency={selectedCurrency}
      />
      
      {/* 하단 네비게이션 바 */}
      <NavigationBar />
    </div>
  );
};

export default GiftShopDetail; 